import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Truck, Mail, Heart } from "lucide-react";
import axios from "axios";
import Navbar from "components/layout/Navbar";
import Footer from "components/layout/Footer";
import { useCart, useAuth, getCartConfig, getAuthConfig } from "App";
import { toast } from "sonner";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { RadioGroup, RadioGroupItem } from "components/ui/radio-group";
import { Textarea } from "components/ui/textarea";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CheckoutPage = () => {
  const navigate = useNavigate();
  const { cart, cartTotal, fetchCart } = useCart();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const mpesaIntervalRef = useRef(null);
  const [pickupAgents, setPickupAgents] = useState([]);
  const [agentSearch, setAgentSearch] = useState("");

  // Form State
  const [customerName, setCustomerName] = useState(user?.name || "");
  const [customerEmail, setCustomerEmail] = useState(user?.email || "");
  const [deliveryMethod, setDeliveryMethod] = useState("pickup_mtaani");
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [address, setAddress] = useState("");
  const [notes, setNotes] = useState("");
  const [mpesaPhone, setMpesaPhone] = useState("");
  const [deliveryFee, setDeliveryFee] = useState(0);
  const [doorstepDestinations, setDoorstepDestinations] = useState([]);
  const [selectedDoorstepDest, setSelectedDoorstepDest] = useState(null);
  const [fetchingFee, setFetchingFee] = useState(false);

  const total = cartTotal + deliveryFee;

  useEffect(() => {
    const fetchAgents = async () => {
      try {
        const response = await axios.get(
          `${API}/delivery/pickup-mtaani/agents`,
        );
        let agentsData = [];
        if (Array.isArray(response.data.agents)) {
          agentsData = response.data.agents;
        } else if (Array.isArray(response.data)) {
          agentsData = response.data;
        } else if (
          response.data?.agents?.data &&
          Array.isArray(response.data.agents.data)
        ) {
          agentsData = response.data.agents.data;
        }
        setPickupAgents(agentsData);
      } catch (error) {
        console.error("Error fetching agents:", error);
        setPickupAgents([]);
      }
    };

    const fetchDoorstepDestinations = async () => {
      try {
        const response = await axios.get(
          `${API}/delivery/doorstep/destinations`,
        );
        setDoorstepDestinations(response.data.destinations || []);
      } catch (error) {
        console.error("Error fetching doorstep destinations:", error);
      }
    };

    fetchAgents();
    fetchDoorstepDestinations();
  }, []);

  useEffect(() => {
    const fetchFee = async () => {
      if (deliveryMethod === "pickup_mtaani" && selectedAgent) {
        setFetchingFee(true);
        try {
          const response = await axios.get(
            `${API}/delivery/pickup-mtaani/charge`,
            {
              params: { destination_agent_id: selectedAgent.id },
            },
          );
          setDeliveryFee(response.data.delivery_fee);
        } catch (error) {
          setDeliveryFee(0);
        } finally {
          setFetchingFee(false);
        }
      }
    };
    fetchFee();
  }, [selectedAgent, deliveryMethod]);

  useEffect(() => {
    const fetchFee = async () => {
      if (deliveryMethod === "doorstep" && selectedDoorstepDest) {
        setFetchingFee(true);
        try {
          const response = await axios.get(`${API}/delivery/doorstep/charge`, {
            params: { destination_id: selectedDoorstepDest.id },
          });
          setDeliveryFee(response.data.delivery_fee);
        } catch (error) {
          setDeliveryFee(350);
        } finally {
          setFetchingFee(false);
        }
      } else if (deliveryMethod === "doorstep" && !selectedDoorstepDest) {
        setDeliveryFee(350);
      }
    };
    fetchFee();
  }, [selectedDoorstepDest, deliveryMethod]);

  useEffect(() => {
    if (user?.name && !customerName) setCustomerName(user.name);
    if (user?.email && !customerEmail) setCustomerEmail(user.email);
  }, [user]);

  const formatPrice = (price) => {
    return new Intl.NumberFormat("en-KE", {
      style: "currency",
      currency: "KES",
      minimumFractionDigits: 0,
    }).format(price);
  };

  const getNextDeliveryDate = () => {
    const date = new Date();
    const day = date.getDay();
    const daysUntilSaturday = day === 6 ? 7 : 6 - day;
    date.setDate(date.getDate() + daysUntilSaturday);
    return date.toLocaleDateString("en-GB", { weekday: 'long', day: 'numeric', month: 'long' });
  };

  const validateAll = () => {
    if (!customerName.trim()) {
      toast.error("Please enter your name");
      return false;
    }
    if (!customerEmail.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerEmail)) {
      toast.error("Please enter a valid email address");
      return false;
    }
    if (deliveryMethod === "pickup_mtaani" && !selectedAgent) {
      toast.error("Please select a pickup location");
      return false;
    }
    if (deliveryMethod === "doorstep" && !address.trim()) {
      toast.error("Please enter your delivery address");
      return false;
    }
    if (!mpesaPhone.match(/^254\d{9}$/)) {
      toast.error("M-Pesa phone must be in format 254XXXXXXXXX");
      return false;
    }
    return true;
  };

  // Clean up M-Pesa polling interval on unmount
  useEffect(() => {
    return () => { if (mpesaIntervalRef.current) clearInterval(mpesaIntervalRef.current); };
  }, []);

  const handleMpesaPayment = async (orderId) => {
    try {
      const response = await axios.post(
        `${API}/payments/mpesa/stk-push?order_id=${orderId}&phone_number=${mpesaPhone}`,
        {},
        getAuthConfig(),
      );
      toast.success(
        response.data.message || "Check your phone for M-Pesa prompt",
      );

      let attempts = 0;
      mpesaIntervalRef.current = setInterval(async () => {
        attempts++;
        try {
          const statusRes = await axios.get(
            `${API}/payments/mpesa/status/${response.data.checkout_request_id}`,
            getAuthConfig(),
          );
          if (statusRes.data.status === "paid") {
            clearInterval(mpesaIntervalRef.current);
            fetchCart();
            navigate(`/order-confirmation?order_id=${orderId}`);
          } else if (statusRes.data.status === "failed" || attempts > 30) {
            clearInterval(mpesaIntervalRef.current);
            toast.error("Payment not completed. Please try again.");
            setLoading(false);
          }
        } catch (e) {
          // Continue polling
        }
      }, 3000);
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Failed to initiate M-Pesa payment",
      );
      setLoading(false);
    }
  };

  const handlePlaceOrder = async () => {
    if (!validateAll()) return;

    setLoading(true);
    try {
      // getCartConfig includes X-Session-ID so the backend can find the guest cart
      const response = await axios.post(
        `${API}/orders`,
        {
          delivery_method: deliveryMethod,
          delivery_address:
            deliveryMethod === "doorstep"
              ? {
                address,
                destination_id: selectedDoorstepDest?.id,
                destination_name: selectedDoorstepDest?.name,
              }
              : null,
          pickup_agent_id: selectedAgent?.id,
          customer_email: customerEmail,
          customer_name: customerName,
          notes,
        },
        getCartConfig(),
      );
      await handleMpesaPayment(response.data.order_id);
    } catch (error) {
      toast.error("Failed to create order");
      setLoading(false);
    }
  };

  if (cart.items?.length === 0) {
    navigate("/cart");
    return null;
  }

  return (
    <div className="bg-[#F4EBE1] text-[#4F3535] min-h-screen">
      <Navbar />

      <div className="pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* Header */}
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="font-serif text-4xl md:text-5xl font-medium text-[#4F3535] leading-[1.1] mb-6">
              Checkout
            </h1>
            <div className="flex items-center justify-center gap-4">
              <div className="h-px bg-[#4F3535]/20 w-16"></div>
              <Heart className="w-4 h-4 text-[#6A4B4B] fill-[#6A4B4B]" />
              <div className="h-px bg-[#4F3535]/20 w-16"></div>
            </div>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Form Section */}
            <div className="lg:col-span-2 space-y-6">
              {/* 1. Your Details + M-Pesa Payment */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="bg-white/60 backdrop-blur-sm p-6 md:p-8 border border-[#EAE2DA]"
              >
                <h2 className="font-serif text-xl text-[#4F3535] mb-6 flex items-center gap-3">
                  <Mail className="w-5 h-5 text-[#6A4B4B]" />
                  Your Details
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label
                      htmlFor="name"
                      className="text-sm tracking-wider uppercase text-[#4F3535]"
                    >
                      Full Name
                    </Label>
                    <Input
                      id="name"
                      value={customerName}
                      onChange={(e) => setCustomerName(e.target.value)}
                      className="input-base mt-2 border-[#EAE2DA] focus:border-[#6A4B4B]"
                      placeholder="Enter your full name"
                      data-testid="checkout-name"
                    />
                  </div>
                  <div>
                    <Label
                      htmlFor="email"
                      className="text-sm tracking-wider uppercase text-[#4F3535]"
                    >
                      Email Address
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      value={customerEmail}
                      onChange={(e) => setCustomerEmail(e.target.value)}
                      className="input-base mt-2 border-[#EAE2DA] focus:border-[#6A4B4B]"
                      placeholder="your@email.com"
                      data-testid="checkout-email"
                    />
                    <p className="text-xs text-[#8A7B7B] mt-1">
                      For your order receipt
                    </p>
                  </div>
                  <div>
                    <Label
                      htmlFor="mpesa-phone"
                      className="text-sm tracking-wider uppercase text-[#4F3535]"
                    >
                      <span className="mpesa-green">M-Pesa</span> Number
                    </Label>
                    <Input
                      id="mpesa-phone"
                      value={mpesaPhone}
                      onChange={(e) => setMpesaPhone(e.target.value)}
                      className="input-base mt-2 border-[#EAE2DA] focus:border-[#6A4B4B]"
                      placeholder="254XXXXXXXXX"
                      data-testid="mpesa-phone"
                    />
                    <p className="text-xs text-[#8A7B7B] mt-1">
                      STK push will be sent here
                    </p>
                  </div>
                </div>
              </motion.div>

              {/* 2. Delivery */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="bg-white/60 backdrop-blur-sm p-6 md:p-8 border border-[#EAE2DA]"
              >
                <h2 className="font-serif text-xl text-[#4F3535] mb-4 flex items-center gap-3">
                  <Truck className="w-5 h-5 text-[#6A4B4B]" />
                  Delivery
                </h2>

                <div className="mb-6 p-4 bg-[#F4EBE1]/50 border border-[#EAE2DA] rounded-sm">
                  <p className="text-sm text-[#4F3535] flex items-center gap-2">
                    <span className="text-lg">✨</span>
                    <span>All orders will be dispatched and delivered on <strong>{getNextDeliveryDate()}</strong>.</span>
                  </p>
                </div>

                <RadioGroup
                  value={deliveryMethod}
                  onValueChange={setDeliveryMethod}
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div
                      className={`p-4 border cursor-pointer transition-all duration-300 ${deliveryMethod === "pickup_mtaani"
                          ? "border-[#6A4B4B] bg-[#6A4B4B]/5"
                          : "border-[#EAE2DA] hover:border-[#6A4B4B]/30"
                        }`}
                      onClick={() => setDeliveryMethod("pickup_mtaani")}
                    >
                      <div className="flex items-start gap-3">
                        <RadioGroupItem
                          value="pickup_mtaani"
                          id="pickup"
                          className="mt-0.5"
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <Label
                              htmlFor="pickup"
                              className="font-medium cursor-pointer text-[#4F3535]"
                            >
                              Pick Up Mtaani
                            </Label>
                            <span className="w-2 h-2 rounded-full bg-[#FCDA45]"></span>
                          </div>
                          <p className="text-sm text-[#8A7B7B] mt-1">
                            Collect from a pickup point
                          </p>
                          <p className="text-sm font-medium text-[#6A4B4B] mt-1">
                            From {formatPrice(150)}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div
                      className={`p-4 border cursor-pointer transition-all duration-300 ${deliveryMethod === "doorstep"
                          ? "border-[#6A4B4B] bg-[#6A4B4B]/5"
                          : "border-[#EAE2DA] hover:border-[#6A4B4B]/30"
                        }`}
                      onClick={() => setDeliveryMethod("doorstep")}
                    >
                      <div className="flex items-start gap-3">
                        <RadioGroupItem
                          value="doorstep"
                          id="doorstep"
                          className="mt-0.5"
                        />
                        <div className="flex-1">
                          <Label
                            htmlFor="doorstep"
                            className="font-medium cursor-pointer text-[#4F3535]"
                          >
                            Doorstep Delivery
                          </Label>
                          <p className="text-sm text-[#8A7B7B] mt-1">
                            Delivered to your address
                          </p>
                          <p className="text-sm font-medium text-[#6A4B4B] mt-1">
                            From {formatPrice(350)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </RadioGroup>

                {/* Pickup location picker */}
                {deliveryMethod === "pickup_mtaani" && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    transition={{ duration: 0.3 }}
                    className="mt-6 pt-6 border-t border-[#EAE2DA]"
                  >
                    <h3 className="text-sm tracking-wider uppercase text-[#4F3535] font-medium mb-4 flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-[#6A4B4B]" />
                      Select Pickup Location
                    </h3>
                    <input
                      type="text"
                      placeholder="Search locations..."
                      onChange={(e) => setAgentSearch(e.target.value)}
                      className="w-full px-4 py-3 text-sm border border-[#EAE2DA] mb-3 focus:outline-none focus:border-[#6A4B4B] text-[#4F3535] placeholder:text-[#8A7B7B]/50 bg-white/50"
                    />
                    <div className="space-y-2 max-h-56 overflow-y-auto">
                      {Array.isArray(pickupAgents) &&
                        pickupAgents
                          .filter(
                            (agent) =>
                              (agent?.business_name || "")
                                .toLowerCase()
                                .includes(agentSearch?.toLowerCase() || "") ||
                              (
                                agent?.loc?.name ||
                                agent?.loc?.description ||
                                ""
                              )
                                .toLowerCase()
                                .includes(agentSearch?.toLowerCase() || ""),
                          )
                          .map((agent) => (
                            <div
                              key={agent?.id}
                              onClick={() => setSelectedAgent(agent)}
                              className={`p-4 border cursor-pointer transition-all duration-300 ${selectedAgent?.id === agent?.id
                                  ? "border-[#6A4B4B] bg-[#6A4B4B]/5"
                                  : "border-[#EAE2DA] hover:border-[#6A4B4B]/30"
                                }`}
                              data-testid={`agent-${agent?.id}`}
                            >
                              <p className="font-medium text-[#4F3535]">
                                {agent?.business_name}
                              </p>
                              <p className="text-sm text-[#8A7B7B]">
                                {agent?.loc?.name ||
                                  agent?.loc?.description ||
                                  "Location details unavailable"}
                              </p>
                            </div>
                          ))}
                    </div>
                  </motion.div>
                )}

                {/* Doorstep address */}
                {deliveryMethod === "doorstep" && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    transition={{ duration: 0.3 }}
                    className="mt-6 pt-6 border-t border-[#EAE2DA]"
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label
                          htmlFor="doorstep-dest"
                          className="text-sm tracking-wider uppercase text-[#4F3535]"
                        >
                          Town/Area *
                        </Label>
                        <select
                          id="doorstep-dest"
                          className="input-base w-full mt-2 border-[#EAE2DA]"
                          value={selectedDoorstepDest?.id || ""}
                          onChange={(e) => {
                            const dest = doorstepDestinations.find(
                              (d) => String(d.id) === e.target.value,
                            );
                            setSelectedDoorstepDest(dest);
                          }}
                        >
                          <option value="">Select your area...</option>
                          {doorstepDestinations.map((dest) => (
                            <option key={dest.id} value={dest.id}>
                              {dest.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <Label
                          htmlFor="address"
                          className="text-sm tracking-wider uppercase text-[#4F3535]"
                        >
                          Specific Address *
                        </Label>
                        <Textarea
                          id="address"
                          value={address}
                          onChange={(e) => setAddress(e.target.value)}
                          placeholder="House No, Street, Building..."
                          className="input-base mt-2 border-[#EAE2DA]"
                          rows={2}
                          data-testid="delivery-address"
                        />
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Order Notes — inline */}
                <div className="mt-6 pt-6 border-t border-[#EAE2DA]">
                  <Label
                    htmlFor="notes"
                    className="text-sm tracking-wider uppercase text-[#4F3535]"
                  >
                    Order Notes{" "}
                    <span className="normal-case italic text-[#8A7B7B]">
                      — optional
                    </span>
                  </Label>
                  <Textarea
                    id="notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Any special instructions..."
                    className="input-base mt-2 border-[#EAE2DA]"
                    rows={2}
                    data-testid="order-notes"
                  />
                </div>
              </motion.div>

              {/* Place Order Button */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                <button
                  onClick={handlePlaceOrder}
                  disabled={loading}
                  className="w-full bg-[#6A4B4B] text-white hover:bg-[#4F3535] transition-all duration-300 py-4 tracking-[0.2em] uppercase text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="pay-now-btn"
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-3">
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                      Processing...
                    </span>
                  ) : (
                    `Place Order & Pay ${formatPrice(total)}`
                  )}
                </button>
              </motion.div>
            </div>

            {/* Order Summary Sidebar */}
            <div className="lg:col-span-1">
              <div className="bg-white/60 backdrop-blur-sm border border-[#EAE2DA] p-6 sticky top-28">
                <h2 className="font-serif text-xl text-[#4F3535] mb-6">
                  Order Summary
                </h2>

                {/* Items */}
                <div className="space-y-4 mb-6 max-h-64 overflow-y-auto pr-2">
                  {cart.items?.map((item) => (
                    <div
                      key={`${item.product_id}-${item.size}`}
                      className="flex gap-3"
                    >
                      <img
                        src={
                          item.image ||
                          "https://images.unsplash.com/photo-1596484552993-aec4311d3381?w=100"
                        }
                        alt={item.name}
                        className="w-16 h-20 object-cover"
                      />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-[#4F3535] line-clamp-1">
                          {item.name}
                        </p>
                        <p className="text-xs text-[#8A7B7B]">
                          Qty: {item.quantity}
                        </p>
                        <p className="text-sm font-medium text-[#4F3535]">
                          {formatPrice(item.price * item.quantity)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="space-y-3 border-t border-[#EAE2DA] pt-4 mb-4">
                  <div className="flex justify-between text-sm text-[#8A7B7B]">
                    <span>Subtotal</span>
                    <span>{formatPrice(cartTotal)}</span>
                  </div>
                  <div className="flex justify-between text-sm text-[#8A7B7B]">
                    <span>Delivery</span>
                    <span className="flex items-center gap-2">
                      {fetchingFee && (
                        <span className="w-3 h-3 border-2 border-[#6A4B4B] border-t-transparent rounded-full animate-spin"></span>
                      )}
                      {formatPrice(deliveryFee)}
                    </span>
                  </div>
                </div>

                <div className="border-t border-[#EAE2DA] pt-4">
                  <div className="flex justify-between font-semibold text-[#4F3535] text-lg">
                    <span>Total</span>
                    <span className="price-tag">{formatPrice(total)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CheckoutPage;
